import React, { useState, useEffect } from "react";
import { Observer } from "mobx-react";
import { useStores } from "../../store";
import { FiAlertTriangle } from "react-icons/fi";
import { BsCheckCircleFill, BsXCircleFill } from "react-icons/bs";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import nointerviewsimage from "../../assests/nointerviewshere.png";
import { Link } from "react-router-dom";
import { MdArrowCircleRight } from "react-icons/md";

export default function Previous({ from, filterTopic, handleOnClick }) {
  const { UserStore, PopupStore, InterviewStore } = useStores();
  const [currentFilter, setCurrentFilter] = useState(null);
  const [ismobile, setismobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setismobile(window.innerWidth < 1000);
    UserStore.hitAllApis();
  }, []);

  useEffect(() => {
    setCurrentFilter(filterTopic);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [filterTopic]);

  const timeStampToDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isReportAvailable = () => {
    return (
      <div className="flex items-center gap-2 text-yellow-500">
        <FiAlertTriangle className="text-lg" />
        <p className="text-sm">Incomplete</p>
      </div>
    );
  };

  const CircularProgress = ({ percentage }) => {
    const size = 45;
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;
    
    const getColor = (value) => {
      if (value < 40) return '#EF4444';
      if (value < 70) return '#F59E0B';
      return '#10B981';
    };

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            className="text-gray-100"
            strokeWidth={strokeWidth}
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            style={{
              stroke: getColor(percentage),
              transition: 'stroke-dashoffset 0.5s ease-in-out, stroke 0.5s ease-in-out'
            }}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
        </svg>
        <div className="absolute flex flex-col items-center justify-center">
          <span className="text-xs font-bold text-gray-800" style={{ color: getColor(percentage) }}>
            {percentage}%
          </span>
        </div>
      </div>
    );
  };

  const renderScore = (interview) => {
    if (interview?.isInterviewCompleted && interview?.isResultPublished && from !== "interview") {
      const score = typeof interview?.result[0]?.score === 'number' && !isNaN(interview?.result[0]?.score) ? parseFloat(interview?.result[0].score.toFixed(1)) : 0;
      return (
        <div className="flex items-center justify-center py-2">
          <CircularProgress percentage={score} />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center py-2">
        <span className="text-sm text-gray-400">--</span>
      </div>
    );
  };

  const renderStatus = (interview) => {
    // Base styles with fixed width and space-between alignment
    const statusStyle = "flex items-center justify-between px-4 py-1.5 rounded-full w-[120px]";

    if (interview?.isInterviewCompleted && interview?.isResultPublished) {
      const score = typeof interview?.result[0]?.score === 'number' && !isNaN(interview?.result[0]?.score) ? parseFloat(interview?.result[0].score.toFixed(1)) : 0;
      if (score >= 70) {
        return (
          <div className={`${statusStyle} bg-green-50`}>
            <BsCheckCircleFill className="text-green-500 text-lg" />
            <span className="text-sm font-medium text-green-700">
              Passed
            </span>
          </div>
        );
      } else {
        return (
          <div className={`${statusStyle} bg-red-50`}>
            <BsXCircleFill className="text-red-500 text-lg" />
            <span className="text-sm font-medium text-red-600">
              Failed
            </span>
          </div>
        );
      }
    }

    if (interview?.isInterviewCompleted && !interview?.isResultPublished) {
      return (
        <div className={`${statusStyle} bg-blue-50`}>
          <BiDotsHorizontalRounded className="text-blue-500 text-xl" />
          <span className="text-sm font-medium text-blue-600">
            Processing
          </span>
        </div>
      );
    }

    if (!interview?.isInterviewCompleted && interview?.isStarted) {
      return (
        <div className={`${statusStyle} bg-orange-50`}>
          <FiAlertTriangle className="text-orange-500 text-lg" />
          <span className="text-sm font-medium text-orange-700">
            In Progress
          </span>
        </div>
      );
    }

    // Not Started status
    return (
      <div className={`${statusStyle} bg-gray-50`}>
        <FiAlertTriangle className="text-gray-400 text-lg" />
        <span className="text-sm font-medium text-gray-600">
          Not Started
        </span>
      </div>
    );
  };

  const Pagination = ({ totalItems, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex justify-between sm:hidden w-full">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
              ${currentPage === 1 
                ? 'text-gray-400 bg-gray-50 cursor-not-allowed' 
                : 'text-gray-700 bg-white hover:bg-gray-50'}`}
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
              ${currentPage === totalPages 
                ? 'text-gray-400 bg-gray-50 cursor-not-allowed' 
                : 'text-gray-700 bg-white hover:bg-gray-50'}`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">
                {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}
              </span>
              {' '}-{' '}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, totalItems)}
              </span>
              {' '}of{' '}
              <span className="font-medium">{totalItems}</span> results
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium
                  ${currentPage === 1 
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >
                <MdKeyboardArrowLeft className="h-5 w-5" />
              </button>
              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                    ${pageNum === currentPage
                      ? 'z-10 bg-primary text-white border-primary'
                      : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  {pageNum}
                </button>
              ))}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium
                  ${currentPage === totalPages 
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >
                <MdKeyboardArrowRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const getPageData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  return (
    <Observer>
      {() => {
        const filteredData = from === "interview"
          ? InterviewStore.previousInterviewsList?.filter(
              (interview) =>
                !interview?.isInterviewCompleted &&
                !interview?.isResultPublished
            )
          : UserStore.dashboardInterviews
              ?.slice()
              ?.reverse()
              ?.filter((interview) =>
                currentFilter
                  ? interview.technology.includes(currentFilter)
                  : true
              );

        const paginatedData = getPageData(filteredData || []);

        return (
          <div className="w-full max-w-7xl mx-auto">
            {UserStore?.dashboardInterviews.length === 0 && from !== "interview" ? (
              <div className="flex flex-col items-center justify-center py-12">
                <img
                  src={nointerviewsimage}
                  className="w-48 h-48 md:w-64 md:h-64 rounded-full mb-6"
                  alt="No interviews"
                />
                <p className="text-xl text-primary font-medium">
                  No Previous Interviews
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg shadow">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {from !== "interview" && (
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            #
                          </th>
                        )}
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Topic
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Level
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Questions
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Score
                        </th>
                        {from !== "interview" && (
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedData.map((interview, index) => (
                        <tr
                          key={interview._id}
                          className={`hover:bg-gray-50 transition-colors ${
                            from === "interview" && !interview?.isInterviewCompleted
                              ? "cursor-pointer"
                              : "cursor-default"
                          }`}
                          onClick={() => {
                            if (from === "interview" && !interview?.isInterviewCompleted) {
                              handleOnClick(interview?._id);
                            }
                          }}
                        >
                          {from !== "interview" && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {String(UserStore?.dashboardInterviews?.length - index).padStart(2, '0')}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {interview?.technology}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-primary">
                              {interview?.levelOfInterview}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {interview?.noOfQuestions}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {timeStampToDate(interview?.createdAt)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderStatus(interview)}
                          </td>
                          <td className="px-6 py-4">
                            {renderScore(interview)}
                          </td>
                          {from !== "interview" && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <Link
                                to={ismobile ? `/report/${interview?._id}` : `/dashboard/report/${interview?._id}`}
                                className="flex items-center gap-1 text-primary hover:text-primary-dark transition-colors"
                              >
                                <span className="font-medium">View</span>
                                <MdArrowCircleRight className="text-lg" />
                              </Link>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  totalItems={filteredData?.length || 0}
                  currentPage={currentPage}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            )}
          </div>
        );
      }}
    </Observer>
  );
}
